import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play, Sparkles, X } from 'lucide-react';
import { getAudioContext, getMasterGain } from '@/audio/AudioEngine';
import { availableLayers, challengeCheck, challengeHint, challengeRow, countLabel, feelCheck, guideGhosts, layerComplete, recordCoachPass, startsOnChallenge } from '@/audio/drumCoach';
import { adaptGroove, grooveById, groovesForTempo, grooveToSteps, kitRowAvailability } from '@/audio/grooves';
import { playDrumHit } from '@/audio/instrumentVoices';
import { convertDrumGrid, linkedCount, resizePattern, sanitizePattern } from '@/audio/patternModel';
import { getKits, getSoundBuffer, loadProducerSounds } from '@/audio/producerSounds';
import { PianoRoll } from './PianoRoll';

const nativeControl = (target) => ['INPUT', 'SELECT', 'BUTTON', 'TEXTAREA'].includes(target?.tagName) || target?.isContentEditable;
const GUARDED_KEYS = new Set(['s', 'v', 'l', '1', '2', '3', '4', '5', '6', '7', '8', '9', ' ', 'Spacebar', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Delete', 'Backspace']);

export function guardPatternEditorKey(event, onPlayToggle) {
  if (event.metaKey || event.ctrlKey || event.altKey || nativeControl(event.target)) return false;
  if (!GUARDED_KEYS.has(event.key) && event.code !== 'Space') return false;
  event.preventDefault();
  event.stopPropagation();
  if (event.code === 'Space' || event.key === ' ' || event.key === 'Spacebar') onPlayToggle?.();
  return true;
}

const labelFor = (rowId) => ({ kick: 'Kick', snare: 'Snare', clap: 'Clap', hat: 'Hat', openhat: 'Open hat', perc: 'Perc', rim: 'Rim', crash: 'Crash' }[rowId] || rowId);

export function starterGroove(style, pattern) {
  const kind = String(style || 'trap').toLowerCase().replace(/[^a-z]/g, '');
  const length = pattern.patternBars * 16;
  const result = {};
  for (const row of pattern.rows) result[row.rowId] = Array(length).fill(0);
  const put = (row, indices, velocity = 1) => indices.forEach((base) => { for (let bar = 0; bar < pattern.patternBars; bar += 1) if (result[row]) result[row][bar * 16 + base] = velocity; });
  if (kind === 'boombap') { put('kick', [0, 10]); put('snare', [4, 12]); put('hat', [0, 2, 4, 6, 8, 10, 12, 14], 0.75); }
  else if (kind === 'rnb' || kind === 'rb') { put('kick', [0, 10]); put('clap', [4, 12]); put('hat', Array.from({ length: 16 }, (_, i) => i), 0.55); }
  else if (kind === 'latin') { put('kick', [0, 4, 8, 12]); put('snare', [10, 11, 14]); put('hat', [0, 2, 4, 6, 8, 10, 12, 14], 0.7); }
  else { put('kick', [0, 6]); put('snare', [4, 12]); put('clap', [4, 12]); put('hat', [0, 2, 4, 6, 8, 10, 12, 14], 0.75); }
  return result;
}

export function interpolatedSteps(previousStep, currentStep) {
  const from = Math.min(Number(previousStep), Number(currentStep));
  const to = Math.max(Number(previousStep), Number(currentStep));
  if (!Number.isInteger(from) || !Number.isInteger(to)) return [];
  return Array.from({ length: to - from + 1 }, (_, index) => from + index);
}

const readToggle = (key) => { try { return globalThis.localStorage?.getItem(key) === '1'; } catch { return false; } };
const saveToggle = (key, value) => { try { globalThis.localStorage?.setItem(key, value ? '1' : '0'); } catch { /* Device storage is optional. */ } };

export function DrumGrid({ clip, pattern, bpm = 120, onCommit, onMakeUnique, playheadBar, isPlaying, onPlayToggle, onAuditionNoteOn }) {
  const [kits, setKits] = useState(getKits());
  const [draft, setDraft] = useState(() => sanitizePattern('drums', pattern));
  const [paintMode, setPaintMode] = useState('draw');
  const picker = useMemo(() => groovesForTempo(bpm), [bpm]);
  const [selectedGrooveId, setSelectedGrooveId] = useState(() => pattern.grooveId || groovesForTempo(bpm).fits[0]?.id || 'trap');
  const [coachOpen, setCoachOpen] = useState(() => readToggle('ba_drum_coach_open'));
  const [countOpen, setCountOpen] = useState(() => readToggle('ba_drum_count_open'));
  const [coachMode, setCoachMode] = useState('guide');
  const [layerIndex, setLayerIndex] = useState(0);
  const [coachText, setCoachText] = useState('Pick a groove and build it one sound at a time.');
  const [ghostMode, setGhostMode] = useState('guide');
  const [challengeFails, setChallengeFails] = useState(0);
  const [passiveTip, setPassiveTip] = useState('');
  const [mergeNotice, setMergeNotice] = useState('');
  const [menu, setMenu] = useState(null);
  const dragRef = useRef(null);
  const draftRef = useRef(draft);
  const committedRef = useRef(JSON.stringify(draft));
  const incomingRef = useRef(sanitizePattern('drums', pattern));
  const previewingRef = useRef(false);
  const tipTimerRef = useRef(null);
  const longPressRef = useRef(null);
  useEffect(() => { const next = sanitizePattern('drums', pattern); setDraft(next); draftRef.current = next; incomingRef.current = next; committedRef.current = JSON.stringify(next); previewingRef.current = false; }, [pattern]);
  useEffect(() => setSelectedGrooveId(pattern.grooveId || picker.fits[0]?.id || 'trap'), [clip.loop_id, pattern.grooveId, picker]);
  useEffect(() => { let live = true; loadProducerSounds().then(() => { if (live) setKits([...getKits()]); }); return () => { live = false; }; }, []);
  useEffect(() => () => { clearTimeout(longPressRef.current); clearTimeout(tipTimerRef.current); }, []);
  const kit = kits.find((candidate) => candidate.id === draft.kitId) || kits[0] || null;
  const kitRows = useMemo(() => new Map((kit?.rows || []).map((row) => [row.id, row])), [kit]);
  const availableRows = useMemo(() => kitRowAvailability(draft, { drumKits: kits }), [draft, kits]);
  const baseGroove = grooveById(selectedGrooveId) || picker.fits[0] || null;
  const groove = useMemo(() => adaptGroove(baseGroove, bpm), [baseGroove, bpm]);
  const gridMismatch = Boolean(groove && draft.stepsPerBar !== groove.stepsPerBar);
  const coachLayers = useMemo(() => availableLayers(groove, availableRows), [groove, availableRows]);
  const missingLayer = useMemo(() => groove?.layers.find((layer) => !availableRows.has(layer.row)) || null, [groove, availableRows]);
  const displayedRows = useMemo(() => {
    const byId = new Map(draft.rows.map((row) => [row.rowId, row]));
    const ordered = (kit?.rows || []).map((row) => byId.get(row.id)).filter(Boolean);
    return [...ordered, ...draft.rows.filter((row) => !(kit?.rows || []).some((kitRow) => kitRow.id === row.rowId))];
  }, [draft.rows, kit]);
  const fullGhost = useMemo(() => groove ? grooveToSteps(groove, availableRows, draft.patternBars) : { steps: {}, ratchets: {} }, [groove, availableRows, draft.patternBars]);
  const layerGhost = useMemo(() => groove ? guideGhosts(groove, layerIndex, draft.patternBars, availableRows) : { steps: {}, ratchets: {} }, [groove, layerIndex, draft.patternBars, availableRows]);
  const challengeGhostRow = groove ? challengeRow(groove, availableRows) : null;
  const ghost = gridMismatch ? { steps: {}, ratchets: {} } : ghostMode === 'full' ? fullGhost : ghostMode === 'challenge'
    ? { steps: { [challengeGhostRow]: fullGhost.steps[challengeGhostRow] || [] }, ratchets: {} }
    : ghostMode === 'guide' && coachMode === 'guide' ? layerGhost : { steps: {}, ratchets: {} };
  const currentStep = isPlaying && playheadBar >= clip.start_bar
    ? Math.floor((((playheadBar - clip.start_bar + Number(clip.offset || 0)) % draft.patternBars + draft.patternBars) % draft.patternBars) * draft.stepsPerBar)
    : -1;

  const updateDraft = useCallback((mutate) => {
    const next = sanitizePattern('drums', mutate(structuredClone(draftRef.current)));
    draftRef.current = next; setDraft(next); return next;
  }, []);
  const commit = useCallback((next = draftRef.current) => {
    const clean = sanitizePattern('drums', next);
    const signature = JSON.stringify(clean);
    if (signature === committedRef.current) return;
    committedRef.current = signature;
    incomingRef.current = clean;
    previewingRef.current = false;
    onCommit(clean);
    clearTimeout(tipTimerRef.current);
    tipTimerRef.current = setTimeout(() => setPassiveTip(feelCheck(clean, groove, availableRows)[0].text), 180);
  }, [onCommit, groove, availableRows]);

  const prepareForGroove = useCallback((source, targetGroove) => {
    let next = sanitizePattern('drums', source);
    if (next.stepsPerBar !== targetGroove.stepsPerBar) next = convertDrumGrid(next, targetGroove.stepsPerBar).pattern;
    if (next.patternBars < (targetGroove.grooveBars || 1)) next = resizePattern('drums', next, targetGroove.grooveBars);
    return next;
  }, []);

  const previewGuide = useCallback((targetGroove) => {
    if (!targetGroove) return;
    if (incomingRef.current.stepsPerBar !== targetGroove.stepsPerBar) {
      const original = incomingRef.current;
      draftRef.current = original; setDraft(original); previewingRef.current = false;
      return;
    }
    const next = prepareForGroove(incomingRef.current, targetGroove);
    draftRef.current = next; setDraft(next);
    previewingRef.current = JSON.stringify(next) !== committedRef.current;
  }, [prepareForGroove]);

  useEffect(() => {
    if (!groove) return;
    setLayerIndex(0); setChallengeFails(0); setGhostMode('guide');
    const faded = startsOnChallenge(groove.family);
    setCoachMode(faded ? 'challenge' : 'guide');
    setCoachText(missingLayer ? `This kit has no ${missingLayer.row} sound. Skip this one.` : faded ? groove.challenge.prompt : groove.about || 'Start with the anchor sounds.');
    if (coachOpen && !faded) previewGuide(groove);
  }, [selectedGrooveId, bpm, coachOpen, groove, missingLayer, previewGuide]);

  useEffect(() => {
    if (gridMismatch || coachMode !== 'guide' || !groove || !coachLayers[layerIndex]) return;
    if (!layerComplete(draft, groove, layerIndex, availableRows)) return;
    const completed = coachLayers[layerIndex];
    setCoachText(completed.line);
    if (layerIndex + 1 < coachLayers.length) setLayerIndex((index) => index + 1);
    else { setCoachMode('challenge'); setGhostMode(null); setCoachText(groove.challenge.prompt); }
  }, [draft, gridMismatch, coachMode, groove, layerIndex, coachLayers, availableRows]);
  useEffect(() => {
    const finish = () => { clearTimeout(longPressRef.current); if (dragRef.current) { dragRef.current = null; commit(); } };
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
    return () => { window.removeEventListener('pointerup', finish); window.removeEventListener('pointercancel', finish); };
  }, [commit]);

  const paint = useCallback((rowId, steps, mode, soft = false) => updateDraft((next) => {
    for (const step of Array.isArray(steps) ? steps : [steps]) {
      next.steps[rowId][step] = mode === 'erase' ? 0 : soft ? 0.55 : 1;
      if (mode === 'erase') delete next.ratchets[rowId][step];
    }
    return next;
  }), [updateDraft]);

  const beginCell = (event, rowId, step) => {
    if (event.button === 2 && event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      setMenu({ rowId, step, x: event.clientX, y: event.clientY });
      return;
    }
    if (event.button !== 0 && event.button !== 2) return;
    event.preventDefault();
    const isCurrentGhost = ghost.steps?.[rowId]?.[step] > 0;
    if (previewingRef.current && !isCurrentGhost) {
      const original = incomingRef.current;
      if (step >= original.patternBars * original.stepsPerBar) return;
      draftRef.current = original; setDraft(original); previewingRef.current = false;
    }
    const existing = draftRef.current.steps[rowId][step] > 0;
    const mode = event.button === 2 ? 'erase' : paintMode === 'erase' || existing ? 'erase' : 'draw';
    const soft = event.button === 0 && event.shiftKey;
    dragRef.current = { rowId, mode, soft, lastStep: step };
    paint(rowId, step, mode, soft);
    if (isCurrentGhost && coachMode === 'guide' && groove) updateDraft((next) => ({ ...next, grooveId: groove.id }));
    if (event.pointerType === 'touch') {
      longPressRef.current = setTimeout(() => {
        dragRef.current = null;
        setMenu({ rowId, step, x: event.clientX, y: event.clientY });
      }, 500);
    }
  };
  const enterCell = (rowId, step) => {
    const drag = dragRef.current;
    if (!drag || (drag.rowId === rowId && drag.lastStep === step)) return;
    const steps = interpolatedSteps(drag.lastStep, step);
    drag.rowId = rowId;
    drag.lastStep = step;
    paint(rowId, steps, drag.mode, drag.soft);
  };
  const openMenu = (event, rowId, step) => {
    event.preventDefault(); event.stopPropagation();
    if (event.shiftKey) setMenu({ rowId, step, x: event.clientX, y: event.clientY });
  };
  const menuAction = (action) => {
    const { rowId, step } = menu;
    const next = updateDraft((value) => {
      if (action === 'erase') { value.steps[rowId][step] = 0; delete value.ratchets[rowId][step]; }
      else if (action === 'soft') value.steps[rowId][step] = 0.55;
      else { value.steps[rowId][step] = value.steps[rowId][step] || 1; value.ratchets[rowId][step] = Number(action); }
      return value;
    });
    setMenu(null); commit(next);
  };
  const audition = async (soundId) => {
    const rowId = draftRef.current.rows.find((row) => row.soundId === soundId)?.rowId;
    const pitch = ({ kick: 36, snare: 38, clap: 39, hat: 42, openhat: 46, perc: 45, rim: 37, crash: 49 })[rowId];
    if (onAuditionNoteOn && pitch) { onAuditionNoteOn(pitch, 1, { token: `drum-editor-${rowId}` }); return; }
    try {
      const buffer = await getSoundBuffer(soundId);
      if (!buffer) return;
      const ctx = getAudioContext(); if (ctx.state === 'suspended') await ctx.resume();
      playDrumHit(ctx, getMasterGain(), buffer, { when: ctx.currentTime, velocity: 1, volume: 1 });
    } catch (error) { console.warn('Drum audition failed:', error.message); }
  };
  const changeKit = (kitId) => {
    const nextKit = kits.find((candidate) => candidate.id === kitId);
    const next = updateDraft((value) => { value.kitId = kitId; value.rows.forEach((row) => { row.soundId = nextKit?.rows?.find((item) => item.id === row.rowId)?.sounds?.[0]?.id || null; }); return value; });
    commit(next);
  };

  const changeGrid = () => {
    const destination = draft.stepsPerBar === 16 ? 12 : 16;
    const converted = convertDrumGrid(incomingRef.current, destination);
    const next = updateDraft(() => converted.pattern);
    setMergeNotice(converted.mergedHits > 0 && destination === 12 ? `${converted.mergedHits} hits merged into the triplet grid.` : '');
    commit(next);
  };

  const switchGrooveGrid = () => {
    if (!groove || incomingRef.current.stepsPerBar === groove.stepsPerBar) return;
    const converted = convertDrumGrid(incomingRef.current, groove.stepsPerBar);
    const next = updateDraft(() => converted.pattern);
    setMergeNotice(converted.mergedHits > 0 && groove.stepsPerBar === 12 ? `${converted.mergedHits} hits merged into the triplet grid.` : '');
    commit(next);
  };

  const useGroove = () => {
    if (!groove) return;
    const prepared = prepareForGroove(incomingRef.current, groove);
    const built = grooveToSteps(groove, availableRows, prepared.patternBars);
    const next = sanitizePattern('drums', { ...prepared, steps: built.steps, ratchets: built.ratchets, grooveId: groove.id, swing: groove.swing });
    draftRef.current = next; setDraft(next); commit(next); setGhostMode(null);
  };

  const checkChallenge = () => {
    const result = challengeCheck(draftRef.current, groove, availableRows);
    if (result.passed) {
      const count = recordCoachPass(groove.family);
      setCoachMode('fade'); setGhostMode(null); setChallengeFails(0);
      setCoachText(count >= 3 ? 'You have this feel. I will start with the challenge next time.' : 'That groove holds up. Nice.');
    } else {
      const fails = challengeFails + 1; setChallengeFails(fails);
      setCoachText(challengeHint(result, groove.stepsPerBar));
    }
  };

  const startChallenge = () => { setCoachMode('challenge'); setGhostMode(null); setCoachText(groove.challenge.prompt); };
  const advanceLayer = () => {
    if (layerIndex + 1 < coachLayers.length) setLayerIndex((index) => index + 1);
    else startChallenge();
  };

  const toggleCoach = () => setCoachOpen((open) => {
    saveToggle('ba_drum_coach_open', !open);
    if (open && previewingRef.current) { const original = incomingRef.current; draftRef.current = original; setDraft(original); previewingRef.current = false; }
    return !open;
  });
  const toggleCount = () => setCountOpen((open) => { saveToggle('ba_drum_count_open', !open); return !open; });
  const currentLayer = coachLayers[layerIndex];

  if (kits.length === 0) return <div className="pattern-empty" data-testid="no-producer-sounds">No sounds loaded yet</div>;
  return <div className="drum-editor">
    <div className="drum-toolbar">
      <label>Kit <select title="Choose a drum kit" value={kit?.id || ''} onChange={(event) => changeKit(event.target.value)}>{kits.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
      <label title={draft.stepsPerBar === 12 ? 'Swing is off on a triplet grid' : 'Delay every other sixteenth note'}>Swing <input title={draft.stepsPerBar === 12 ? 'Swing is off on a triplet grid' : 'Delay every other sixteenth note'} disabled={draft.stepsPerBar === 12} type="range" min="0" max="0.6" step="0.01" value={draft.swing} onChange={(event) => setDraft(updateDraft((value) => ({ ...value, swing: Number(event.target.value) })))} onPointerUp={() => commit()} onBlur={() => commit()} /> {Math.round(draft.swing * 100)}%</label>
      <button title="Draw hits on touch screens" className={paintMode === 'draw' ? 'active' : ''} onClick={() => setPaintMode('draw')}>Draw</button>
      <button title="Erase hits on touch screens" className={paintMode === 'erase' ? 'active' : ''} onClick={() => setPaintMode('erase')}>Erase</button>
      <button title="Clear every drum hit" onClick={() => commit(updateDraft((value) => { Object.keys(value.steps).forEach((rowId) => { value.steps[rowId] = value.steps[rowId].map(() => 0); value.ratchets[rowId] = {}; }); return value; }))}>Clear</button>
      <button className={coachOpen ? 'active' : ''} onClick={toggleCoach}><Sparkles size={14} /> Pizzul coach</button>
      <button className={countOpen ? 'active' : ''} onClick={toggleCount}>Count</button>
      <button className={draft.stepsPerBar === 12 ? 'active' : ''} onClick={changeGrid}>Triplet grid</button>
      <span className="drum-mouse-hint">Right-drag erases. Shift+right-click opens hit options.</span>
    </div>
    {coachOpen && <div className="drum-coach-strip" data-testid="drum-coach-strip">
      <img src="/pizzul/thinking.png" alt="Pizzul" />
      <label>Groove <select aria-label="Groove" value={selectedGrooveId} onChange={(event) => {
        const id = event.target.value; setSelectedGrooveId(id);
        try { globalThis.localStorage?.setItem('ba_coach_last_family', grooveById(id)?.family || ''); } catch { /* Device storage is optional. */ }
      }}>
        {picker.fits.length > 0 && <optgroup label={picker.fitsLabel}>{picker.fits.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</optgroup>}
        {Object.entries(picker.families).map(([family, items]) => <optgroup key={family} label={family}>{items.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</optgroup>)}
      </select></label>
      <strong className="coach-level">{coachMode === 'fade' ? 'Challenge passed' : coachMode === 'challenge' ? 'Challenge' : `Guide ${Math.min(layerIndex + 1, coachLayers.length)}/${coachLayers.length}`}</strong>
      <span className="coach-line" role="status">{gridMismatch ? `This groove uses the ${groove.stepsPerBar === 12 ? 'triplet' : '16-step'} grid.` : coachText}</span>
      {gridMismatch ? <button onClick={switchGrooveGrid}>Switch grid</button> : <>
        {coachMode === 'guide' && currentLayer?.layer === 'flavor' && <button onClick={() => setGhostMode('guide')}>Add flavor</button>}
        {coachMode === 'guide' && currentLayer?.layer === 'flavor' && <button onClick={startChallenge}>I&apos;m good</button>}
        {coachMode === 'guide' && currentLayer?.layer !== 'flavor' && <button onClick={advanceLayer}>Skip</button>}
        {coachMode === 'guide' && <button onClick={startChallenge}>Try it without help</button>}
        {coachMode === 'challenge' && <button onClick={checkChallenge}>Check</button>}
        {coachMode === 'challenge' && challengeFails >= 2 && <button onClick={() => setGhostMode('challenge')}>Show me</button>}
        {(coachMode === 'challenge' || coachMode === 'fade') && <button onClick={() => { setCoachMode('guide'); setLayerIndex(0); setGhostMode('guide'); previewGuide(groove); }}>Guide me</button>}
        <button onClick={() => setGhostMode((mode) => { if (mode === 'full') return null; previewGuide(groove); return 'full'; })}>{ghostMode === 'full' ? 'Hide' : 'Show me'}</button>
        {ghostMode === 'full' && <button onClick={useGroove}>Use it</button>}
        <button onClick={() => setCoachText(feelCheck(draftRef.current, groove, availableRows)[0].text)}>Check my beat</button>
        {passiveTip && <span className="coach-passive-tip">{passiveTip}</span>}
      </>}
    </div>}
    {mergeNotice && <div className="drum-merge-notice" role="status">{mergeNotice}</div>}
    <div className="drum-grid-scroll" data-testid="drum-grid-surface" tabIndex={0} onKeyDown={(event) => guardPatternEditorKey(event, onPlayToggle)}>
      <div className="drum-grid" style={{ '--steps': draft.patternBars * draft.stepsPerBar }}>
        <div className="drum-corner" />
        <div className="drum-step-numbers">{Array.from({ length: draft.patternBars * draft.stepsPerBar }, (_, step) => {
          const local = step % draft.stepsPerBar; const group = draft.stepsPerBar === 12 ? 3 : 4; const label = countOpen ? countLabel(local, draft.stepsPerBar) : step + 1;
          return <span key={step} className={local === 0 ? 'bar beat-number' : local % group === 0 ? 'beat beat-number' : ''}>{label}</span>;
        })}</div>
        {displayedRows.map((row) => {
          const sourceRow = kitRows.get(row.rowId);
          const missing = !sourceRow?.sounds?.some((sound) => sound.id === row.soundId);
          const disabled = !sourceRow?.sounds?.length;
          return <React.Fragment key={row.rowId}>
            <div className={`drum-row-head${disabled ? ' disabled' : ''}`}>
              <button title={`Hear ${labelFor(row.rowId)}`} disabled={disabled || missing} onClick={() => audition(row.soundId)}>{labelFor(row.rowId)}</button>
              <select title={`Choose ${labelFor(row.rowId)} sound`} value={missing ? '' : row.soundId || ''} onChange={(event) => { const next = updateDraft((value) => { value.rows.find((item) => item.rowId === row.rowId).soundId = event.target.value || null; return value; }); commit(next); audition(event.target.value); }}>
                {missing && <option value="">Sound missing</option>}{(sourceRow?.sounds || []).map((sound) => <option key={sound.id} value={sound.id}>{sound.name}</option>)}
              </select>
              <button title={`Mute ${labelFor(row.rowId)}`} className={row.muted ? 'active' : ''} onClick={() => commit(updateDraft((value) => { const found = value.rows.find((item) => item.rowId === row.rowId); found.muted = !found.muted; return value; }))}>M</button>
              <input aria-label={`${labelFor(row.rowId)} volume`} title={`${labelFor(row.rowId)} volume`} type="range" min="0" max="1" step="0.01" value={row.volume} onChange={(event) => updateDraft((value) => { value.rows.find((item) => item.rowId === row.rowId).volume = Number(event.target.value); return value; })} onPointerUp={() => commit()} onBlur={() => commit()} />
            </div>
            <div className={`drum-steps${disabled ? ' disabled' : ''}`}>{draft.steps[row.rowId].map((velocity, step) => {
              const ticks = draft.ratchets[row.rowId]?.[step] || 0;
              const local = step % draft.stepsPerBar; const group = draft.stepsPerBar === 12 ? 3 : 4;
              return <button key={step} title={`${labelFor(row.rowId)} step ${step + 1}. Right-drag to erase. Shift+right-click for hit options.`} className={`${velocity ? 'on' : ''}${velocity && velocity < 0.8 ? ' soft' : ''}${currentStep === step ? ' current' : ''}${!velocity && ghost.steps?.[row.rowId]?.[step] ? ' ghost' : ''}${Math.floor(local / group) % 2 ? ' group-alt' : ''}${local === 0 ? ' bar' : local % group === 0 ? ' beat' : ''}`}
                disabled={disabled} onPointerDown={(event) => beginCell(event, row.rowId, step)} onPointerEnter={() => enterCell(row.rowId, step)} onContextMenu={(event) => openMenu(event, row.rowId, step)}>
                {ticks > 1 && <span className="ratchet-ticks">{Array.from({ length: ticks }, (_, i) => <i key={i} />)}</span>}
              </button>;
            })}</div>
          </React.Fragment>;
        })}
      </div>
    </div>
    {menu && <><div className="fixed inset-0 z-40" onPointerDown={() => setMenu(null)} /><div className="pattern-context" style={{ left: menu.x, top: menu.y }}>
      <button onClick={() => menuAction('erase')}>Delete</button><button onClick={() => menuAction('soft')}>Soft hit</button>
      {[2, 3, 4].map((count) => <button key={count} onClick={() => menuAction(String(count))}>Roll x{count}</button>)}
      <button onClick={() => { setMenu(null); onMakeUnique?.(); }}>Make unique</button>
    </div></>}
  </div>;
}

export function PatternEditor({ clip, tracks, bpm, keySignature, playheadBar, isPlaying, onClose, onCommitPattern, onMakeUnique, onPlayPattern, onStopArrangement, onKeySignatureChange, recording = false, recordingClipId, liveOctave, onOctaveChange, onAuditionNoteOn, onAuditionNoteOff, onWriteMelody, onWriteBass }) {
  const [pianoHeaderHost, setPianoHeaderHost] = useState(null);
  const receivePianoHeaderHost = useCallback((node) => setPianoHeaderHost(node), []);
  const pattern = sanitizePattern(clip.patternType, clip.pattern);
  const count = linkedCount(tracks, clip.loop_id);
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape' && !event.metaKey && !event.ctrlKey) onClose(); };
    window.addEventListener('keydown', close); return () => window.removeEventListener('keydown', close);
  }, [onClose]);
  return <section className="pattern-editor" data-testid="pattern-editor">
    <header className="pattern-editor-header">
      <strong>{clip.name}</strong><span className="pattern-badge">{clip.patternType}</span>
      <label>Length <select title="Pattern length" value={pattern.patternBars} onChange={(event) => onCommitPattern(resizePattern(clip.patternType, pattern, Number(event.target.value)))}>{[1, 2, 4, 8].map((bars) => <option key={bars} value={bars}>{bars} bar{bars === 1 ? '' : 's'}</option>)}</select></label>
      <button title="Loop and play this pattern" onClick={onPlayPattern}>{isPlaying ? <Pause size={14} /> : <Play size={14} />} Play pattern</button>
      {recording && recordingClipId === clip.id && <span className="recording" data-testid="editor-recording-indicator">● Recording</span>}
      {clip.patternType !== 'drums' && <div className="pattern-header-portal" ref={receivePianoHeaderHost} />}
      {count > 1 && <><span data-testid="linked-count">Linked: {count} copies</span><button title="Stop edits from changing the linked copies" onClick={onMakeUnique}>Make unique</button></>}
      <span className="pattern-editor-spacer" />
      <button title="Close pattern editor" aria-label="Close pattern editor" onClick={onClose}><X size={16} /></button>
    </header>
    {clip.patternType === 'drums'
      ? <DrumGrid clip={clip} pattern={pattern} bpm={bpm} onCommit={onCommitPattern} onMakeUnique={onMakeUnique} playheadBar={playheadBar} isPlaying={isPlaying} onPlayToggle={onPlayPattern} onStopArrangement={onStopArrangement} onAuditionNoteOn={onAuditionNoteOn} />
      : <PianoRoll clip={{ ...clip, playheadBar, isPlaying }} tracks={tracks} pattern={pattern} bpm={bpm} keySignature={keySignature} headerHost={pianoHeaderHost} onKeySignatureChange={onKeySignatureChange} onCommit={onCommitPattern} onPlayToggle={onPlayPattern} onStopArrangement={onStopArrangement} recording={recording && recordingClipId === clip.id} liveOctave={liveOctave} onOctaveChange={onOctaveChange} onAuditionNoteOn={onAuditionNoteOn} onAuditionNoteOff={onAuditionNoteOff} onWriteMelody={onWriteMelody} onWriteBass={onWriteBass} />}
  </section>;
}

export default PatternEditor;
